import {
    File as FileIcon,
    FileArchive,
    FileAudio,
    FileCode,
    FileImage,
    FileSpreadsheet,
    FileText,
    FileType2,
    FileVideo,
    Folder,
    Presentation,
    type LucideIcon
} from "lucide-react";
import type { DriveItem } from "@/api/driveApi";

export type FileCategory =
    | "folder"
    | "document"
    | "spreadsheet"
    | "presentation"
    | "pdf"
    | "image"
    | "video"
    | "audio"
    | "archive"
    | "text"
    | "code"
    | "other";

const EXT: Record<Exclude<FileCategory, "folder" | "other">, string[]> = {
    document: ["doc", "docx", "odt", "rtf", "sfdt", "dotx"],
    spreadsheet: ["xls", "xlsx", "xlsm", "ods", "csv", "tsv"],
    presentation: ["ppt", "pptx", "odp", "pps", "ppsx"],
    pdf: ["pdf"],
    image: ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico", "avif", "heic"],
    video: ["mp4", "webm", "mov", "mkv", "avi", "m4v", "ogv"],
    audio: ["mp3", "wav", "ogg", "oga", "m4a", "flac", "aac", "opus"],
    archive: ["zip", "rar", "7z", "tar", "gz", "tgz", "bz2", "xz"],
    text: ["txt", "md", "markdown", "log", "rst"],
    code: [
        "json", "xml", "yaml", "yml", "toml", "ini", "env", "js", "mjs", "cjs", "ts", "tsx", "jsx", "css", "scss",
        "html", "htm", "py", "cs", "java", "c", "h", "cpp", "hpp", "go", "rs", "rb", "php", "sh", "ps1", "bat",
        "sql", "kt", "swift", "vue", "csproj", "props", "config"
    ]
};

const EXT_TO_CATEGORY = new Map<string, FileCategory>();
for (const [cat, exts] of Object.entries(EXT)) {
    for (const e of exts) EXT_TO_CATEGORY.set(e, cat as FileCategory);
}

export function extensionOf(name: string): string {
    return name.toLowerCase().match(/\.([^./\\]+)$/)?.[1] ?? "";
}

/** Name without its extension — used to pre-select the base name when renaming. */
export function baseNameLength(name: string): number {
    const dot = name.lastIndexOf(".");
    return dot > 0 ? dot : name.length;
}

export function categoryOf(item: Pick<DriveItem, "kind" | "name" | "contentType">): FileCategory {
    if (item.kind === "Folder") return "folder";
    const byExt = EXT_TO_CATEGORY.get(extensionOf(item.name));
    if (byExt) return byExt;
    const ct = (item.contentType ?? "").toLowerCase();
    if (ct === "application/pdf") return "pdf";
    if (ct.startsWith("image/")) return "image";
    if (ct.startsWith("video/")) return "video";
    if (ct.startsWith("audio/")) return "audio";
    if (ct.startsWith("text/")) return "text";
    if (ct.includes("zip") || ct.includes("compressed")) return "archive";
    return "other";
}

interface CategoryStyle {
    icon: LucideIcon;
    /** Icon colour (light + dark). */
    color: string;
    /** Soft tinted background for grid-card previews. */
    tint: string;
    label: string;
}

export const CATEGORY_STYLE: Record<FileCategory, CategoryStyle> = {
    folder: { icon: Folder, color: "text-amber-500 fill-amber-400/30 dark:text-amber-400", tint: "bg-amber-50 dark:bg-amber-950/30", label: "Folder" },
    document: { icon: FileText, color: "text-blue-600 dark:text-blue-400", tint: "bg-blue-50 dark:bg-blue-950/40", label: "Document" },
    spreadsheet: { icon: FileSpreadsheet, color: "text-emerald-600 dark:text-emerald-400", tint: "bg-emerald-50 dark:bg-emerald-950/40", label: "Spreadsheet" },
    presentation: { icon: Presentation, color: "text-orange-500 dark:text-orange-400", tint: "bg-orange-50 dark:bg-orange-950/40", label: "Presentation" },
    pdf: { icon: FileType2, color: "text-red-600 dark:text-red-400", tint: "bg-red-50 dark:bg-red-950/40", label: "PDF" },
    image: { icon: FileImage, color: "text-purple-600 dark:text-purple-400", tint: "bg-purple-50 dark:bg-purple-950/40", label: "Image" },
    video: { icon: FileVideo, color: "text-pink-600 dark:text-pink-400", tint: "bg-pink-50 dark:bg-pink-950/40", label: "Video" },
    audio: { icon: FileAudio, color: "text-cyan-600 dark:text-cyan-400", tint: "bg-cyan-50 dark:bg-cyan-950/40", label: "Audio" },
    archive: { icon: FileArchive, color: "text-yellow-700 dark:text-yellow-400", tint: "bg-yellow-50 dark:bg-yellow-950/30", label: "Archive" },
    text: { icon: FileText, color: "text-slate-500 dark:text-slate-400", tint: "bg-slate-50 dark:bg-slate-900/60", label: "Text" },
    code: { icon: FileCode, color: "text-teal-600 dark:text-teal-400", tint: "bg-teal-50 dark:bg-teal-950/40", label: "Code" },
    other: { icon: FileIcon, color: "text-slate-400 dark:text-slate-500", tint: "bg-slate-50 dark:bg-slate-900/60", label: "File" }
};

/** Human label for the details pane ("PDF", "Word document (.docx)"...). */
export function typeLabel(item: DriveItem): string {
    const cat = categoryOf(item);
    if (cat === "folder") return "Folder";
    const ext = extensionOf(item.name);
    const base = CATEGORY_STYLE[cat].label;
    return ext ? `${base} (.${ext})` : base;
}

// ── Type filter chips ────────────────────────────────────────────────

export type TypeFilter =
    | "folders"
    | "documents"
    | "spreadsheets"
    | "presentations"
    | "pdfs"
    | "images"
    | "videos"
    | "archives";

export const TYPE_FILTERS: { id: TypeFilter; label: string; category: FileCategory }[] = [
    { id: "folders", label: "Folders", category: "folder" },
    { id: "documents", label: "Documents", category: "document" },
    { id: "spreadsheets", label: "Spreadsheets", category: "spreadsheet" },
    { id: "presentations", label: "Presentations", category: "presentation" },
    { id: "pdfs", label: "PDFs", category: "pdf" },
    { id: "images", label: "Images", category: "image" },
    { id: "videos", label: "Videos", category: "video" },
    { id: "archives", label: "Archives", category: "archive" }
];

export function matchesFilter(item: DriveItem, filter: TypeFilter | null): boolean {
    if (!filter) return true;
    const want = TYPE_FILTERS.find((f) => f.id === filter)?.category;
    const cat = categoryOf(item);
    // Plain-text / code files read as documents for filtering purposes.
    if (filter === "documents") return cat === "document" || cat === "text";
    return cat === want;
}

// ── Preview + open-in-app ────────────────────────────────────────────

export type PreviewKind = "image" | "pdf" | "video" | "audio" | "text" | "office" | "none";

/** Text previews are capped so a huge log never locks the tab. */
export const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

export type OfficeKind = "docx" | "xlsx" | "pptx";

export function officeKindOf(item: DriveItem): OfficeKind | null {
    if (item.kind !== "File") return null;
    const ext = extensionOf(item.name);
    if (["doc", "docx", "rtf", "odt"].includes(ext)) return "docx";
    if (["xls", "xlsx", "xlsm", "ods", "csv"].includes(ext)) return "xlsx";
    if (["ppt", "pptx", "odp"].includes(ext)) return "pptx";
    return null;
}

export function previewKindOf(item: DriveItem): PreviewKind {
    if (item.kind !== "File") return "none";
    const cat = categoryOf(item);
    const ext = extensionOf(item.name);
    if (cat === "image") return "image";
    if (cat === "pdf") return "pdf";
    if (cat === "video") return "video";
    if (cat === "audio") return "audio";
    // CSV / TSV read better as text than as a PDF rendering of a sheet.
    if (cat === "text" || cat === "code" || ext === "csv" || ext === "tsv") {
        return item.sizeBytes <= TEXT_PREVIEW_MAX_BYTES ? "text" : "none";
    }
    if (officeKindOf(item)) return "office";
    return "none";
}

export const OFFICE_APP_NAME: Record<OfficeKind, string> = {
    docx: "Docs",
    xlsx: "Sheets",
    pptx: "Slides"
};

function env(key: string, fallback: string): string {
    const raw = (import.meta.env as unknown as Record<string, string | undefined>)[key];
    return raw && raw.trim().length > 0 ? raw.trim() : fallback;
}

export const APP_URLS = {
    docs: env("VITE_DOCS_WEB_URL", "http://localhost:5210"),
    sheets: env("VITE_SHEET_WEB_URL", "http://localhost:5220"),
    slides: env("VITE_PRESENT_WEB_URL", "http://localhost:5232"),
    /** Athavita PDF (EnterprisePdf/web) — opens Drive files as `/?fileId=<id>`. */
    pdf: env("VITE_PDF_WEB_URL", "http://localhost:5250")
};

/** The Athavita app "Open in …" launches for this file (Docs / Sheets / Slides / PDF editor), or null. */
export function openWithAppName(item: DriveItem): string | null {
    const office = officeKindOf(item);
    if (office) return OFFICE_APP_NAME[office];
    if (item.kind === "File" && categoryOf(item) === "pdf") return "PDF editor";
    return null;
}

export function officeAppUrl(kind: OfficeKind): string {
    return kind === "docx" ? APP_URLS.docs : kind === "xlsx" ? APP_URLS.sheets : APP_URLS.slides;
}

/** Server-side Office→PDF converter base (…/api/docs/, …/api/sheet/, …/api/present/). */
export function officeServiceUrl(kind: OfficeKind): string | undefined {
    const key = kind === "docx" ? "VITE_DOCS_SERVICE_URL" : kind === "xlsx" ? "VITE_SHEET_SERVICE_URL" : "VITE_PRESENT_SERVICE_URL";
    const raw = (import.meta.env as unknown as Record<string, string | undefined>)[key];
    return raw && raw.trim().length > 0 ? raw.trim() : undefined;
}
